import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import TableC from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import {
  useFoodContractsReport,
  useFoodContractsReportMutation,
} from "@/services/reports";
import { cn, filterObject } from "@/utils/helpers";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Container, Spinner } from "reactstrap";

function pickContractNumber(row) {
  return (
    row.contract_id ??
    row.contract_number ??
    row.id ??
    null
  );
}

function pickStart(row) {
  return (
    row.contract_start ??
    row.start_date ??
    row.start ??
    row.contract_start_date ??
    null
  );
}

function pickEnd(row) {
  return (
    row.contract_end ??
    row.end_date ??
    row.end ??
    row.contract_end_date ??
    null
  );
}

function formatReportDateTime(val) {
  if (val == null || val === "") return "—";
  const d = dayjs(val);
  return d.isValid() ? d.format("D/M/YYYY H:mm") : String(val);
}

function pickPilgrimCount(row) {
  const v =
    row.pilgrims_count ??
    row.pilgrims ??
    row.number_of_pilgrims ??
    row.pilgrim_count ??
    0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pickServiceLocation(row) {
  const v =
    row.service_location ??
    row.location ??
    row.camp ??
    row.service_site ??
    row.site_name;
  return v != null && v !== "" ? String(v) : "—";
}

function pickContractor(row, getLocalizedText) {
  const c = row.contractor ?? row.provider;
  if (c && typeof c === "object") {
    const label = getLocalizedText(c.name_ar, c.name_en);
    return label || "—";
  }
  const s =
    row.contractor_name ??
    row.company_name ??
    row.client ??
    (typeof c === "string" ? c : null);
  return s != null && s !== "" ? String(s) : "—";
}

export const FoodContractsReport = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { getLocalizedText } = useLocalizedText();

  const {
    mutate: generateFoodContractsReport,
    isPending: isGeneratingFoodContractsReport,
  } = useFoodContractsReportMutation();

  const form = useForm({
    defaultValues: {
      format: "pdf",
      orientation: "portrait",
    },
    disabled: isGeneratingFoodContractsReport,
  });

  const format = form.watch("format");
  const isJson = format === "json";

  const filters = filterObject({
    ...form.getValues(),
    format: "json",
    page,
  });

  const {
    data: foodContractsRes,
    isLoading: isLoadingFoodContracts,
    refetch,
    isRefetching: isRefetchingFoodContracts,
  } = useFoodContractsReport({
    params: filters,
  });

  const rows = foodContractsRes?.data?.data || [];
  const meta = foodContractsRes?.data?.meta || {};

  const {
    control,
    formState: { errors },
    getValues,
  } = form;

  const formText = {
    json: { buttonText: t("VIEW_REPORTS") },
    pdf: { buttonText: t("EXPORT_PDF") },
    excel: { buttonText: t("EXPORT_EXCEL") },
  };

  const formContent = formText[format] || formText.json;

  useEffect(() => {
    if (isJson && page > 1) {
      refetch();
    }
  }, [page, isJson, refetch]);

  const ctrls = [
    {
      labelProps: { children: t("REPORT_FORMAT") },
      controllerProps: {
        name: "format",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="food-contracts-format"
            placeholder={t("REPORT_FORMAT")}
            status={error ? "error" : undefined}
            options={[
              { value: "json", label: t("VIEW_REPORTS") },
              { value: "pdf", label: t("EXPORT_PDF") },
              { value: "excel", label: t("EXPORT_EXCEL") },
            ]}
          />
        ),
      },
    },
    !isJson && {
      labelProps: { children: t("ORIENTATION") },
      controllerProps: {
        name: "orientation",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="food-contracts-orientation"
            placeholder={t("ORIENTATION")}
            status={error ? "error" : undefined}
            options={[
              { value: "portrait", label: t("PORTRAIT") },
              { value: "landscape", label: t("LANDSCAPE") },
            ]}
          />
        ),
      },
    },
    {
      labelProps: { children: t("CONTRACT_ID") },
      controllerProps: {
        name: "contract_id",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="food-contracts-contract_id"
            placeholder={t("CONTRACT_ID")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("REQUEST_ID") },
      controllerProps: {
        name: "request_id",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="food-contracts-request_id"
            placeholder={t("REQUEST_ID")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("CONTRACT_START_FROM") },
      controllerProps: {
        name: "contract_start_from",
        render: ({ field, fieldState: { error } }) => (
          <DatePicker
            style={{ height: "40px" }}
            allowClear
            id="food-contracts-contract_start_from"
            className="w-100"
            placeholder={t("CONTRACT_START_FROM")}
            status={error ? "error" : undefined}
            format="YYYY-MM-DD"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) =>
              field.onChange(date ? date.format("YYYY-MM-DD") : null)
            }
          />
        ),
      },
    },
    {
      labelProps: { children: t("CONTRACT_START_TO") },
      controllerProps: {
        name: "contract_start_to",
        render: ({ field, fieldState: { error } }) => (
          <DatePicker
            style={{ height: "40px" }}
            allowClear
            id="food-contracts-contract_start_to"
            className="w-100"
            placeholder={t("CONTRACT_START_TO")}
            status={error ? "error" : undefined}
            format="YYYY-MM-DD"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) =>
              field.onChange(date ? date.format("YYYY-MM-DD") : null)
            }
          />
        ),
      },
    },
    {
      labelProps: { children: t("CONTRACT_END_FROM") },
      controllerProps: {
        name: "contract_end_from",
        render: ({ field, fieldState: { error } }) => (
          <DatePicker
            style={{ height: "40px" }}
            allowClear
            id="food-contracts-contract_end_from"
            className="w-100"
            placeholder={t("CONTRACT_END_FROM")}
            status={error ? "error" : undefined}
            format="YYYY-MM-DD"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) =>
              field.onChange(date ? date.format("YYYY-MM-DD") : null)
            }
          />
        ),
      },
    },
    {
      labelProps: { children: t("CONTRACT_END_TO") },
      controllerProps: {
        name: "contract_end_to",
        render: ({ field, fieldState: { error } }) => (
          <DatePicker
            style={{ height: "40px" }}
            allowClear
            id="food-contracts-contract_end_to"
            className="w-100"
            placeholder={t("CONTRACT_END_TO")}
            status={error ? "error" : undefined}
            format="YYYY-MM-DD"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) =>
              field.onChange(date ? date.format("YYYY-MM-DD") : null)
            }
          />
        ),
      },
    },
    {
      labelProps: { children: t("ORGANIZER") },
      controllerProps: {
        name: "mission_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            allowClear
            showSearch
            id="food-contracts-mission_id"
            placeholder={t("ORGANIZER")}
            status={error ? "error" : undefined}
            optionKey="missions"
            options={(m) => ({
              value: m.id,
              label: getLocalizedText(m.name_ar, m.name_en),
            })}
          />
        ),
      },
    },
    {
      labelProps: { children: t("AFFILIATED_ORGANIZER") },
      controllerProps: {
        name: "company_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            allowClear
            showSearch
            id="food-contracts-company_id"
            placeholder={t("AFFILIATED_ORGANIZER")}
            status={error ? "error" : undefined}
            optionKey="companies"
            options={(c) => ({
              value: c.id,
              label: getLocalizedText(c.name_ar, c.name_en),
            })}
          />
        ),
      },
    },
    {
      labelProps: { children: t("SERVICE_CENTER") },
      controllerProps: {
        name: "service_center_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            allowClear
            id="food-contracts-service_center_id"
            placeholder={t("SERVICE_CENTER")}
            status={error ? "error" : undefined}
            optionKey="service_centers"
            options={(sc) => ({
              value: sc.id,
              label: getLocalizedText(sc.name_ar, sc.name_en),
            })}
          />
        ),
      },
    },
  ].filter(Boolean);

  const pageSize = meta?.per_page || 10;

  const totalOnPage = useMemo(
    () => rows.reduce((sum, row) => sum + pickPilgrimCount(row), 0),
    [rows]
  );

  const footerTotal = useMemo(() => {
    const fromMeta =
      meta?.total_pilgrims ??
      meta?.pilgrims_total ??
      meta?.sum_pilgrims ??
      foodContractsRes?.data?.total_pilgrims;
    if (fromMeta != null && fromMeta !== "") {
      const n = Number(fromMeta);
      return Number.isFinite(n) ? n : totalOnPage;
    }
    return totalOnPage;
  }, [meta, foodContractsRes, totalOnPage]);

  const columns = useMemo(
    () => [
      {
        name: t("FOOD_CONTRACTS_COL_SERIAL"),
        key: "_serial",
        filterable: false,
        render: (_, _row, index) => (page - 1) * pageSize + index + 1,
      },
      {
        name: t("FOOD_CONTRACTS_COL_CONTRACT_NUMBER"),
        key: "_contract_number",
        filterable: false,
        render: (_, row) => pickContractNumber(row) ?? "—",
      },
      {
        name: t("FOOD_CONTRACTS_COL_START_DATE"),
        key: "_start",
        filterable: false,
        render: (_, row) => formatReportDateTime(pickStart(row)),
      },
      {
        name: t("FOOD_CONTRACTS_COL_END_DATE"),
        key: "_end",
        filterable: false,
        render: (_, row) => formatReportDateTime(pickEnd(row)),
      },
      {
        name: t("FOOD_CONTRACTS_COL_NUMBER_PILGRIMS"),
        key: "_pilgrims",
        filterable: false,
        render: (_, row) => pickPilgrimCount(row),
      },
      {
        name: t("FOOD_CONTRACTS_COL_SERVICE_LOCATION"),
        key: "_location",
        filterable: false,
        render: (_, row) => pickServiceLocation(row),
      },
      {
        name: t("FOOD_CONTRACTS_COL_CONTRACTOR"),
        key: "_contractor",
        filterable: false,
        render: (_, row) => pickContractor(row, getLocalizedText),
      },
    ],
    [t, getLocalizedText, page, pageSize]
  );

  const onSubmit = (data) => {
    const payload = {
      ...getValues(),
      page,
    };

    if (data.format === "json") {
      setPage(1);
      return refetch();
    }

    generateFoodContractsReport({ params: payload });
  };

  const backTo =
    window.history.length > 1 ? -1 : `/app/madina/housing-reports`;

  const tableLoading =
    isLoadingFoodContracts || isRefetchingFoodContracts;
  const isLoading =
    isGeneratingFoodContractsReport || isLoadingFoodContracts;

  return (
    <div>
      <PageHeader
        title={t("FOOD_CONTRACTS_REPORT_TITLE")}
        sticky={false}
      />
      <Container fluid>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mb-4">
          <Controllers
            watch={form.watch}
            controllers={ctrls.map((config) => ({
              ...config,
              labelProps: {
                ...config.labelProps,
                className: cn("fw-bold", config.labelProps.className),
              },
              controllerProps: {
                ...config.controllerProps,
                control,
                errors,
              },
              layout: {
                colProps: { xs: 6, md: 6 },
              },
            }))}
          />
          <ActionsFooter>
            <Button
              disabled={isLoading}
              className="btn btn-primary btn-sm"
              type="submit"
            >
              {isLoading ? <Spinner size="sm" /> : formContent.buttonText}
            </Button>
            <Link
              to={backTo}
              disabled={isLoading}
              style={{ pointerEvents: isLoading ? "none" : "auto" }}
              className="btn btn-danger btn-sm"
              type="button"
            >
              {t("CANCEL")}
            </Link>
            <button
              onClick={() => form.reset()}
              disabled={isLoading}
              className="btn btn-outline-primary btn-sm"
              type="button"
            >
              {t("RESET")}
            </button>
          </ActionsFooter>
        </form>

        {isJson && (
          <>
            <TableC
              columnsFilter={false}
              data={rows}
              columns={columns}
              loading={tableLoading}
              skeletonCount={10}
              pagination={{
                current: page,
                total: meta?.total || 0,
                pageSize: meta?.per_page || 10,
                onChange: (newPage) => setPage(newPage),
              }}
            />
            {rows.length > 0 && (
              <div className="fw-bold py-2 px-3 bg-white border rounded-bottom mb-3">
                {t("FOOD_CONTRACTS_FOOTER_TOTAL_PILGRIMS")}{" "}
                {footerTotal.toLocaleString()}
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};
